/**
 * ShapeAnnotationManager
 * 
 * Separate class for managing shape annotations on images.
 * Supports rectangle, circle, and star shapes with resizing capabilities.
 * This can be easily removed if shape functionality is not needed.
 */
export type ShapeType = 'rectangle' | 'circle' | 'star';

export interface ShapeElement {
  id: string;
  type: ShapeType;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  strokeWidth: number;
  selected?: boolean;
}

export class ShapeAnnotationManager {
  private shapes: ShapeElement[] = [];
  private shapeCanvas: HTMLCanvasElement | null = null;
  private imageElement: HTMLImageElement | null = null;
  private canvasSize: { width: number; height: number } = { width: 0, height: 0 };
  private selectedShapeId: string | null = null;
  private isDrawing: boolean = false;
  private isResizing: boolean = false;
  private isDragging: boolean = false;
  private resizeHandle: 'nw' | 'ne' | 'sw' | 'se' | null = null;
  private startPos: { x: number; y: number } = { x: 0, y: 0 };
  private currentShape: Partial<ShapeElement> | null = null;
  private dragOffset: { x: number; y: number } = { x: 0, y: 0 };
  // Current tool settings - can be updated from outside
  private currentShapeType: ShapeType | null = null;
  private currentColor: string = '#ff0000';
  private currentStrokeWidth: number = 3;

  /**
   * Initialize the shape annotation manager
   */
  initialize(
    shapeCanvas: HTMLCanvasElement,
    imageElement: HTMLImageElement,
    canvasSize: { width: number; height: number }
  ) {
    this.shapeCanvas = shapeCanvas;
    this.imageElement = imageElement;
    this.canvasSize = canvasSize;
    this.updateCanvasSize();
    this.setupEventListeners();
    // Ensure proper cursor state
    try { this.shapeCanvas.style.cursor = 'crosshair'; } catch {}
    try { console.log('[ShapeManager] initialized', { size: this.canvasSize }); } catch {}
  }

  /**
   * Public: begin drawing at coordinates (called from React handlers)
   */
  beginDraw(x: number, y: number) {
    if (!this.shapeCanvas) return;
    if (!this.currentShapeType) {
      try { console.log('[ShapeManager] beginDraw ignored (no tool)'); } catch {}
      return;
    }
    this.startShape(this.currentShapeType, x, y, this.currentColor, this.currentStrokeWidth);
    try { console.log('[ShapeManager] beginDraw', { x, y, type: this.currentShapeType }); } catch {}
  }

  /**
   * Public: update current drawing to coordinates
   */
  updateDraw(x: number, y: number) {
    if (!this.isDrawing || !this.currentShape) return;
    const width = x - this.startPos.x;
    const height = y - this.startPos.y;
    this.currentShape.width = Math.abs(width);
    this.currentShape.height = Math.abs(height);
    this.currentShape.x = width < 0 ? x : this.startPos.x;
    this.currentShape.y = height < 0 ? y : this.startPos.y;
    this.redraw();
    try { console.log('[ShapeManager] updateDraw', { w: this.currentShape.width, h: this.currentShape.height }); } catch {}
  }

  /**
   * Public: finalize drawing
   */
  endDraw() {
    if (!this.isDrawing || !this.currentShape) return;
    if (this.currentShape.width && this.currentShape.height &&
        this.currentShape.width > 5 && this.currentShape.height > 5) {
      const newShape: ShapeElement = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: this.currentShape.type as ShapeType,
        x: this.currentShape.x || 0,
        y: this.currentShape.y || 0,
        width: this.currentShape.width || 0,
        height: this.currentShape.height || 0,
        color: this.currentShape.color || '#ff0000',
        strokeWidth: this.currentShape.strokeWidth || 3,
      };
      this.shapes.push(newShape);
      this.selectShape(newShape.id);
      try { console.log('[ShapeManager] endDraw', { id: newShape.id, w: newShape.width, h: newShape.height }); } catch {}
    }
    this.currentShape = null;
    this.isDrawing = false;
    this.redraw();
  }

  /**
   * Update canvas size
   */
  updateCanvasSize(canvasSize?: { width: number; height: number }) {
    if (canvasSize) {
      this.canvasSize = canvasSize;
    }
    if (this.shapeCanvas) {
      this.shapeCanvas.width = this.canvasSize.width;
      this.shapeCanvas.height = this.canvasSize.height;
      this.redraw();
    }
  }

  /**
   * Setup event listeners for mouse interactions
   */
  private setupEventListeners() {
    if (!this.shapeCanvas) return;

    const handleMouseDown = this.handleMouseDown.bind(this);
    const handleMouseMove = this.handleMouseMove.bind(this);
    const handleMouseUp = this.handleMouseUp.bind(this);

    this.shapeCanvas.addEventListener('mousedown', handleMouseDown);
    this.shapeCanvas.addEventListener('mousemove', handleMouseMove);
    this.shapeCanvas.addEventListener('mouseup', handleMouseUp);
    this.shapeCanvas.addEventListener('mouseleave', handleMouseUp);
    try { console.log('[ShapeManager] listeners attached'); } catch {}
  }

  /**
   * Handle mouse down
   */
  private handleMouseDown(e: MouseEvent) {
    if (!this.shapeCanvas) return;

    const rect = this.shapeCanvas.getBoundingClientRect();
    const scaleX = this.shapeCanvas.width / rect.width;
    const scaleY = this.shapeCanvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    this.startPos = { x, y };
    try { console.log('[ShapeManager] mousedown', { x, y, tool: this.currentShapeType }); } catch {}

    // Check if clicking on a resize handle
    if (this.selectedShapeId) {
      const shape = this.shapes.find(s => s.id === this.selectedShapeId);
      if (shape) {
        const handle = this.getResizeHandle(x, y, shape);
        if (handle) {
          this.isResizing = true;
          this.resizeHandle = handle;
          return;
        }
      }
    }

    // Check if clicking on existing shape
    const clickedShape = this.getShapeAt(x, y);
    if (clickedShape) {
      this.selectShape(clickedShape.id);
      this.isDragging = true;
      this.dragOffset = {
        x: x - clickedShape.x,
        y: y - clickedShape.y
      };
      try { console.log('[ShapeManager] drag start', { id: clickedShape.id }); } catch {}
      return;
    }

    // Start drawing new shape
    if (this.currentShapeType) {
      this.startShape(this.currentShapeType, x, y, this.currentColor, this.currentStrokeWidth);
      try { console.log('[ShapeManager] draw start', { type: this.currentShapeType, x, y }); } catch {}
    } else {
      try { console.log('[ShapeManager] mousedown ignored (no shape tool selected)'); } catch {}
    }
  }

  /**
   * Handle mouse move
   */
  private handleMouseMove(e: MouseEvent) {
    if (!this.shapeCanvas) return;

    const rect = this.shapeCanvas.getBoundingClientRect();
    const scaleX = this.shapeCanvas.width / rect.width;
    const scaleY = this.shapeCanvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    if (this.isResizing && this.selectedShapeId) {
      const shape = this.shapes.find(s => s.id === this.selectedShapeId);
      if (shape && this.resizeHandle) {
        this.resizeShape(shape, x, y);
        this.redraw();
        try { console.log('[ShapeManager] resizing', { id: shape.id, handle: this.resizeHandle, x, y }); } catch {}
      }
    } else if (this.isDragging && this.selectedShapeId) {
      const shape = this.shapes.find(s => s.id === this.selectedShapeId);
      if (shape) {
        shape.x = x - this.dragOffset.x;
        shape.y = y - this.dragOffset.y;
        // Keep shape within canvas bounds
        shape.x = Math.max(0, Math.min(shape.x, this.canvasSize.width - shape.width));
        shape.y = Math.max(0, Math.min(shape.y, this.canvasSize.height - shape.height));
        this.redraw();
      }
    } else if (this.isDrawing && this.currentShape) {
      // Update current shape while drawing
      const width = x - this.startPos.x;
      const height = y - this.startPos.y;
      this.currentShape.width = Math.abs(width);
      this.currentShape.height = Math.abs(height);
      this.currentShape.x = width < 0 ? x : this.startPos.x;
      this.currentShape.y = height < 0 ? y : this.startPos.y;
      this.redraw();
      try { console.log('[ShapeManager] drawing', { w: this.currentShape.width, h: this.currentShape.height }); } catch {}
    } else {
      // Update cursor based on hover
      const shape = this.getShapeAt(x, y);
      if (shape && shape.id === this.selectedShapeId) {
        const handle = this.getResizeHandle(x, y, shape);
        if (handle) {
          this.shapeCanvas.style.cursor = this.getCursorForHandle(handle);
        } else {
          this.shapeCanvas.style.cursor = 'move';
        }
      } else {
        this.shapeCanvas.style.cursor = 'crosshair';
      }
    }
  }

  /**
   * Handle mouse up
   */
  private handleMouseUp() {
    if (this.isResizing) {
      this.isResizing = false;
      this.resizeHandle = null;
    } else if (this.isDragging) {
      this.isDragging = false;
      this.dragOffset = { x: 0, y: 0 };
    } else if (this.isDrawing && this.currentShape) {
      // Finalize shape
      if (this.currentShape.width && this.currentShape.height && 
          this.currentShape.width > 5 && this.currentShape.height > 5) {
        const newShape: ShapeElement = {
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          type: this.currentShape.type as ShapeType,
          x: this.currentShape.x || 0,
          y: this.currentShape.y || 0,
          width: this.currentShape.width || 0,
          height: this.currentShape.height || 0,
          color: this.currentShape.color || '#ff0000',
          strokeWidth: this.currentShape.strokeWidth || 3,
        };
        this.shapes.push(newShape);
        this.selectShape(newShape.id);
        try { console.log('[ShapeManager] draw end', { id: newShape.id, w: newShape.width, h: newShape.height }); } catch {}
      }
      this.currentShape = null;
      this.isDrawing = false;
      this.redraw();
    }
  }

  /**
   * Set the current shape tool settings
   */
  setToolSettings(type: ShapeType | null, color: string, strokeWidth: number) {
    this.currentShapeType = type;
    this.currentColor = color;
    this.currentStrokeWidth = strokeWidth;
  }

  /**
   * Start drawing a new shape
   */
  startShape(type: ShapeType, x: number, y: number, color: string, strokeWidth: number) {
    this.currentShape = {
      type,
      x,
      y,
      width: 0,
      height: 0,
      color,
      strokeWidth,
    };
    this.startPos = { x, y };
    this.isDrawing = true;
    this.deselectShape();
  }

  /**
   * Get shape at coordinates
   */
  private getShapeAt(x: number, y: number): ShapeElement | null {
    // Check shapes in reverse order (top to bottom)
    for (let i = this.shapes.length - 1; i >= 0; i--) {
      const shape = this.shapes[i];
      if (this.isPointInShape(x, y, shape)) {
        return shape;
      }
    }
    return null;
  }

  /**
   * Check if point is in shape
   */
  private isPointInShape(x: number, y: number, shape: ShapeElement): boolean {
    if (shape.type === 'rectangle') {
      return x >= shape.x && x <= shape.x + shape.width &&
             y >= shape.y && y <= shape.y + shape.height;
    } else if (shape.type === 'circle') {
      const centerX = shape.x + shape.width / 2;
      const centerY = shape.y + shape.height / 2;
      const radiusX = shape.width / 2;
      const radiusY = shape.height / 2;
      const dx = (x - centerX) / radiusX;
      const dy = (y - centerY) / radiusY;
      return dx * dx + dy * dy <= 1;
    } else if (shape.type === 'star') {
      // Approximate star as circle for hit detection
      const centerX = shape.x + shape.width / 2;
      const centerY = shape.y + shape.height / 2;
      const radius = Math.min(shape.width, shape.height) / 2;
      const dx = x - centerX;
      const dy = y - centerY;
      return dx * dx + dy * dy <= radius * radius;
    }
    return false;
  }

  /**
   * Get resize handle at coordinates
   */
  private getResizeHandle(x: number, y: number, shape: ShapeElement): 'nw' | 'ne' | 'sw' | 'se' | null {
    const handleSize = 8;
    const handles = [
      { pos: { x: shape.x, y: shape.y }, type: 'nw' as const },
      { pos: { x: shape.x + shape.width, y: shape.y }, type: 'ne' as const },
      { pos: { x: shape.x, y: shape.y + shape.height }, type: 'sw' as const },
      { pos: { x: shape.x + shape.width, y: shape.y + shape.height }, type: 'se' as const },
    ];

    for (const handle of handles) {
      if (Math.abs(x - handle.pos.x) < handleSize && Math.abs(y - handle.pos.y) < handleSize) {
        return handle.type;
      }
    }
    return null;
  }

  /**
   * Get cursor for resize handle
   */
  private getCursorForHandle(handle: 'nw' | 'ne' | 'sw' | 'se'): string {
    const cursors = {
      nw: 'nw-resize',
      ne: 'ne-resize',
      sw: 'sw-resize',
      se: 'se-resize',
    };
    return cursors[handle];
  }

  /**
   * Resize shape
   */
  private resizeShape(shape: ShapeElement, x: number, y: number) {
    if (!this.resizeHandle) return;

    switch (this.resizeHandle) {
      case 'nw':
        shape.width = shape.width + (shape.x - x);
        shape.height = shape.height + (shape.y - y);
        shape.x = x;
        shape.y = y;
        break;
      case 'ne':
        shape.width = x - shape.x;
        shape.height = shape.height + (shape.y - y);
        shape.y = y;
        break;
      case 'sw':
        shape.width = shape.width + (shape.x - x);
        shape.height = y - shape.y;
        shape.x = x;
        break;
      case 'se':
        shape.width = x - shape.x;
        shape.height = y - shape.y;
        break;
    }

    // Ensure minimum size
    if (shape.width < 10) {
      const diff = 10 - shape.width;
      shape.width = 10;
      if (this.resizeHandle === 'nw' || this.resizeHandle === 'sw') {
        shape.x -= diff;
      }
    }
    if (shape.height < 10) {
      const diff = 10 - shape.height;
      shape.height = 10;
      if (this.resizeHandle === 'nw' || this.resizeHandle === 'ne') {
        shape.y -= diff;
      }
    }
  }

  /**
   * Select a shape
   */
  selectShape(id: string) {
    this.selectedShapeId = id;
    this.shapes.forEach(s => s.selected = s.id === id);
    this.redraw();
  }

  /**
   * Deselect all shapes
   */
  deselectShape() {
    this.selectedShapeId = null;
    this.shapes.forEach(s => s.selected = false);
    this.redraw();
  }

  /**
   * Remove a shape
   */
  removeShape(id: string): boolean {
    const index = this.shapes.findIndex(s => s.id === id);
    if (index !== -1) {
      this.shapes.splice(index, 1);
      if (this.selectedShapeId === id) {
        this.selectedShapeId = null;
      }
      this.redraw();
      return true;
    }
    return false;
  }

  /**
   * Get all shapes
   */
  getShapes(): ShapeElement[] {
    return [...this.shapes];
  }

  /**
   * Clear all shapes
   */
  clear(): void {
    this.shapes = [];
    this.selectedShapeId = null;
    this.redraw();
  }

  /**
   * Redraw shape canvas
   */
  private redraw(): void {
    if (!this.shapeCanvas) return;

    const ctx = this.shapeCanvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, this.shapeCanvas.width, this.shapeCanvas.height);

    // Draw all shapes
    this.shapes.forEach(shape => {
      this.drawShape(ctx, shape);
    });

    // Draw current shape being drawn
    if (this.currentShape && this.currentShape.type) {
      const tempShape: ShapeElement = {
        id: '',
        type: this.currentShape.type,
        x: this.currentShape.x || 0,
        y: this.currentShape.y || 0,
        width: this.currentShape.width || 0,
        height: this.currentShape.height || 0,
        color: this.currentShape.color || '#ff0000',
        strokeWidth: this.currentShape.strokeWidth || 3,
        selected: false,
      };
      ctx.setLineDash([5, 5]);
      this.drawShape(ctx, tempShape);
      ctx.setLineDash([]);
    } else {
      // Tiny border to confirm layer is active
      ctx.save();
      ctx.strokeStyle = 'rgba(255,0,0,0.25)';
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, 0.5, this.shapeCanvas.width - 1, this.shapeCanvas.height - 1);
      ctx.restore();
    }
  }

  /**
   * Draw a shape on canvas
   */
  private drawShape(ctx: CanvasRenderingContext2D, shape: ShapeElement) {
    ctx.strokeStyle = shape.color;
    ctx.lineWidth = shape.strokeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (shape.type === 'rectangle') {
      ctx.strokeRect(shape.x, shape.y, shape.width, shape.height);
    } else if (shape.type === 'circle') {
      const centerX = shape.x + shape.width / 2;
      const centerY = shape.y + shape.height / 2;
      const radiusX = shape.width / 2;
      const radiusY = shape.height / 2;
      ctx.beginPath();
      ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
      ctx.stroke();
    } else if (shape.type === 'star') {
      this.drawStar(ctx, shape);
    }

    // Draw selection handles
    if (shape.selected) {
      this.drawSelectionHandles(ctx, shape);
    }
  }

  /**
   * Draw a star shape
   */
  private drawStar(ctx: CanvasRenderingContext2D, shape: ShapeElement) {
    const centerX = shape.x + shape.width / 2;
    const centerY = shape.y + shape.height / 2;
    const outerRadius = Math.min(shape.width, shape.height) / 2;
    const innerRadius = outerRadius * 0.4;
    const spikes = 5;
    let rot = Math.PI / 2 * 3;
    let x = centerX;
    let y = centerY;
    const step = Math.PI / spikes;

    ctx.beginPath();
    ctx.moveTo(centerX, centerY - outerRadius);
    for (let i = 0; i < spikes; i++) {
      x = centerX + Math.cos(rot) * outerRadius;
      y = centerY + Math.sin(rot) * outerRadius;
      ctx.lineTo(x, y);
      rot += step;

      x = centerX + Math.cos(rot) * innerRadius;
      y = centerY + Math.sin(rot) * innerRadius;
      ctx.lineTo(x, y);
      rot += step;
    }
    ctx.lineTo(centerX, centerY - outerRadius);
    ctx.closePath();
    ctx.stroke();
  }

  /**
   * Draw selection handles
   */
  private drawSelectionHandles(ctx: CanvasRenderingContext2D, shape: ShapeElement) {
    const handleSize = 6;
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = shape.color;
    ctx.lineWidth = 2;

    const handles = [
      { x: shape.x, y: shape.y },
      { x: shape.x + shape.width, y: shape.y },
      { x: shape.x, y: shape.y + shape.height },
      { x: shape.x + shape.width, y: shape.y + shape.height },
    ];

    handles.forEach(handle => {
      ctx.beginPath();
      ctx.arc(handle.x, handle.y, handleSize, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    });
  }

  /**
   * Draw shapes on a canvas context (for saving)
   */
  drawOnCanvas(
    ctx: CanvasRenderingContext2D,
    scaleX: number,
    scaleY: number
  ): void {
    this.shapes.forEach(shape => {
      ctx.strokeStyle = shape.color;
      ctx.lineWidth = shape.strokeWidth * Math.min(scaleX, scaleY);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const scaledX = shape.x * scaleX;
      const scaledY = shape.y * scaleY;
      const scaledWidth = shape.width * scaleX;
      const scaledHeight = shape.height * scaleY;

      if (shape.type === 'rectangle') {
        ctx.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);
      } else if (shape.type === 'circle') {
        const centerX = scaledX + scaledWidth / 2;
        const centerY = scaledY + scaledHeight / 2;
        const radiusX = scaledWidth / 2;
        const radiusY = scaledHeight / 2;
        ctx.beginPath();
        ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
        ctx.stroke();
      } else if (shape.type === 'star') {
        const centerX = scaledX + scaledWidth / 2;
        const centerY = scaledY + scaledHeight / 2;
        const outerRadius = Math.min(scaledWidth, scaledHeight) / 2;
        const innerRadius = outerRadius * 0.4;
        const spikes = 5;
        let rot = Math.PI / 2 * 3;
        let x = centerX;
        let y = centerY;
        const step = Math.PI / spikes;

        ctx.beginPath();
        ctx.moveTo(centerX, centerY - outerRadius);
        for (let i = 0; i < spikes; i++) {
          x = centerX + Math.cos(rot) * outerRadius;
          y = centerY + Math.sin(rot) * outerRadius;
          ctx.lineTo(x, y);
          rot += step;

          x = centerX + Math.cos(rot) * innerRadius;
          y = centerY + Math.sin(rot) * innerRadius;
          ctx.lineTo(x, y);
          rot += step;
        }
        ctx.lineTo(centerX, centerY - outerRadius);
        ctx.closePath();
        ctx.stroke();
      }
    });
  }

  /**
   * Get coordinates relative to the image
   */
  getImageCoordinates(
    clientX: number,
    clientY: number,
    imageRect: DOMRect
  ): { x: number; y: number } {
    return {
      x: clientX - imageRect.left,
      y: clientY - imageRect.top
    };
  }

  /**
   * Public: add a shape programmatically from bounds (used by fallback)
   */
  addShape(
    type: ShapeType,
    x: number,
    y: number,
    width: number,
    height: number,
    color: string,
    strokeWidth: number
  ): string {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const shape: ShapeElement = { id, type, x, y, width, height, color, strokeWidth };
    this.shapes.push(shape);
    this.selectShape(id);
    this.redraw();
    try { console.log('[ShapeManager] addShape', { id, type, x, y, width, height }); } catch {}
    return id;
  }
}
